import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { notifyCustomOrder } from '../services/telegramService';
import '../styles/CustomOrder.css';

const CustomOrder = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    customerName: user?.displayName || '',
    email: user?.email || '',
    phone: '',
    eventType: '',
    eventDescription: '',
    eventDate: '',
    eventTime: '',
    guestCountMin: '',
    guestCountMax: '',
    venue: '',
    specialRequirements: '',
    budget: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const eventTypes = [
    'Wedding',
    'Birthday Party',
    'Corporate Event',
    'Anniversary',
    'Baby Shower',
    'Graduation Party',
    'Religious Ceremony',
    'Festival Celebration',
    'Business Meeting',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        ...formData,
        userId: user?.uid || null,
        userEmail: user?.email || formData.email,
        status: 'pending',
        createdAt: serverTimestamp(),
        guestCount: `${formData.guestCountMin} - ${formData.guestCountMax}`,
        orderType: 'custom'
      };

      const docRef = await addDoc(collection(db, 'customOrders'), orderData);
      
      // Send Telegram notification
      try {
        await notifyCustomOrder(orderData, docRef.id);
      } catch (error) {
        console.error('Failed to send Telegram notification:', error);
        // Don't fail the order if notification fails
      }
      
      setSubmitted(true);
      setFormData({
        customerName: '',
        email: '',
        phone: '',
        eventType: '',
        eventDescription: '',
        eventDate: '',
        eventTime: '',
        guestCountMin: '',
        guestCountMax: '',
        venue: '',
        specialRequirements: '',
        budget: ''
      });
    } catch (error) {
      console.error('Error submitting custom order:', error);
      alert('Failed to submit order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="custom-order-container">
        <div className="success-message">
          <h2>Order Submitted Successfully!</h2>
          <p>Thank you for your custom order request. We will contact you within 24 hours to discuss the details.</p>
          <button 
            className="btn-primary"
            onClick={() => setSubmitted(false)}
          >
            Submit Another Order
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-order-container">
      <div className="custom-order-header">
        <h1>Custom Order Request</h1>
        <p>Planning a special event? Let us create a custom menu just for you!</p>
      </div>

      <form onSubmit={handleSubmit} className="custom-order-form">
        <div className="form-section">
          <h3>Contact Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customerName">Full Name *</label>
              <input
                type="text"
                id="customerName"
                name="customerName"
                value={formData.customerName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                readOnly={!!user}
                style={user ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                required
              />
              {user && <small style={{ color: '#666', fontSize: '0.85rem' }}>Using your account email</small>}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Event Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="eventType">Event Type *</label>
              <select
                id="eventType"
                name="eventType"
                value={formData.eventType}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Event Type</option>
                {eventTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="venue">Venue/Location *</label>
              <input
                type="text"
                id="venue"
                name="venue"
                value={formData.venue}
                onChange={handleInputChange}
                placeholder="Event venue or address"
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="eventDescription">Event Description *</label>
            <textarea
              id="eventDescription"
              name="eventDescription"
              value={formData.eventDescription}
              onChange={handleInputChange}
              placeholder="Describe your event and what kind of food/catering you need..."
              rows="4"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Date & Time</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="eventDate">Event Date *</label>
              <input
                type="date"
                id="eventDate"
                name="eventDate"
                value={formData.eventDate}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="eventTime">Event Time *</label>
              <input
                type="time"
                id="eventTime"
                name="eventTime"
                value={formData.eventTime}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Guest Count</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="guestCountMin">Minimum Guests *</label>
              <input
                type="number"
                id="guestCountMin"
                name="guestCountMin"
                value={formData.guestCountMin}
                onChange={handleInputChange}
                min="1"
                placeholder="e.g., 50"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="guestCountMax">Maximum Guests *</label>
              <input
                type="number"
                id="guestCountMax"
                name="guestCountMax"
                value={formData.guestCountMax}
                onChange={handleInputChange}
                min={formData.guestCountMin || "1"}
                placeholder="e.g., 75"
                required
              />
            </div>
          </div>
          <p className="guest-count-note">
            Guest count range: {formData.guestCountMin || '0'} - {formData.guestCountMax || '0'} people
          </p>
        </div>

        <div className="form-section">
          <h3>Additional Information</h3>
          <div className="form-group">
            <label htmlFor="specialRequirements">Special Requirements</label>
            <textarea
              id="specialRequirements"
              name="specialRequirements"
              value={formData.specialRequirements}
              onChange={handleInputChange}
              placeholder="Dietary restrictions, special themes, equipment needs, etc."
              rows="3"
            />
          </div>
          <div className="form-group">
            <label htmlFor="budget">Budget Range (Optional)</label>
            <input
              type="text"
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleInputChange}
              placeholder="e.g., ₹50,000 - ₹75,000"
            />
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Custom Order Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomOrder;